import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ActionSettings from '../../../library/SimpleControls/ActionSettings';
import * as api from './RoleCapabilityService';
import RbacPageShell from './RbacPageShell';

const emptyForm = { roleCode: '', name: '', type: 'EXTERNAL' as 'INTERNAL' | 'EXTERNAL' };

const RolesPage = () => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState<api.RoleRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api.listRoles(true);
            setRoles(res.data.roles || []);
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return roles;
        return roles.filter(
            (r) =>
                r.name.toLowerCase().includes(q) ||
                r.roleCode.toLowerCase().includes(q) ||
                r.type.toLowerCase().includes(q)
        );
    }, [roles, filter]);

    const openCreate = () => {
        setEditId(null);
        setForm(emptyForm);
        setError('');
        setShowModal(true);
    };

    const openEdit = (role: api.RoleRow) => {
        setEditId(role.id);
        setForm({
            roleCode: role.roleCode,
            name: role.name,
            type: role.type as 'INTERNAL' | 'EXTERNAL',
        });
        setError('');
        setShowModal(true);
    };

    const onSave = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            if (editId == null) {
                await api.createRole(form);
            } else {
                await api.updateRole(editId, { name: form.name, type: form.type });
            }
            setShowModal(false);
            await load();
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (role: api.RoleRow) => {
        if (role.isSystem) return;
        const msg = role.isActive
            ? 'Are you sure you want to Deactivate this record?'
            : 'Are you sure you want to Activate this record?';
        if (!window.confirm(msg)) return;
        setBusy(true);
        setError('');
        try {
            if (role.isActive) await api.deleteRole(role.id);
            else await api.updateRole(role.id, { isActive: true });
            await load();
        } catch (err) {
            setError(api.errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const roleActions = (role: api.RoleRow) => {
        const actions = ['MAP_CAPABILITIES'];
        if (!role.isSystem) {
            actions.push('EDIT');
            actions.push(role.isActive ? 'DEACTIVATE' : 'ACTIVATE');
        }
        return actions;
    };

    const handleAction = (role: api.RoleRow, action: string) => {
        switch (action) {
            case 'MAP_CAPABILITIES':
                navigate(`/support/role-capabilities/${role.id}`);
                break;
            case 'EDIT':
                openEdit(role);
                break;
            case 'DEACTIVATE':
            case 'ACTIVATE':
                if (!busy) toggleActive(role);
                break;
            default:
                break;
        }
    };

    return (
        <RbacPageShell
            title="Manage roles"
            subtitle="Create and maintain INTERNAL / EXTERNAL roles used for access control."
            actions={
                <button type="button" className="btn btn-green" onClick={openCreate}>
                    <span>Add role</span>
                </button>
            }
        >
            {error && !showModal ? (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            ) : null}

            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 filterSearch gap-2">
                <span className="font-14">Found {filtered.length} roles</span>
                <div style={{ minWidth: 240, maxWidth: 360 }} className="flex-grow-1 flex-md-grow-0">
                    <input
                        className="form-control"
                        placeholder="Search by name, code or type…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
            </div>

            {loading ? (
                <p className="mb-0">Loading…</p>
            ) : (
                <div className="table-responsive attendee_dtl">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Code</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((role) => (
                                <tr key={role.id}>
                                    <td>
                                        {role.name}
                                        {role.isSystem ? ' *' : ''}
                                    </td>
                                    <td>{role.roleCode}</td>
                                    <td>{role.type}</td>
                                    <td style={{ color: role.isActive ? 'green' : 'red' }}>
                                        {role.isActive ? 'Active' : 'De-active'}
                                    </td>
                                    <td>
                                        {roleActions(role).length === 1 ? (
                                            <Link
                                                to={`/support/role-capabilities/${role.id}`}
                                                title="Map capabilities"
                                                aria-label={`Map capabilities for ${role.name}`}
                                            >
                                                <i className="icon-setting text-gray-84 pe-0" />
                                            </Link>
                                        ) : (
                                            <ActionSettings
                                                id={role}
                                                actions={roleActions(role)}
                                                onClick={handleAction}
                                            />
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length ? (
                                <tr>
                                    <td colSpan={5} className="text-muted">
                                        No roles match the search.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="text-muted font-13 mt-2 mb-0">
                * System role cannot be edited or deactivated. Deactivate is a soft delete.
            </p>

            {showModal ? (
                <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ background: 'rgba(0,0,0,.4)' }}>
                    <div className="modal-dialog modal-dialog-centered max-600 mx-auto">
                        <div className="modal-content">
                            <div className="modal-header p-4 height-70 border-0 bg-gray-f2">
                                <h5 className="modal-title">{editId == null ? 'Add role' : 'Edit role'}</h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-small"
                                    aria-label="Close"
                                    onClick={() => setShowModal(false)}
                                />
                            </div>
                            <div className="modal-body p-4">
                                {error ? (
                                    <div className="alert alert-danger py-2" role="alert">
                                        {error}
                                    </div>
                                ) : null}
                                <form className="needs-validation" noValidate onSubmit={onSave}>
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <label className="form-label">Role code</label>
                                            <input
                                                className="form-control flex-1"
                                                value={form.roleCode}
                                                disabled={editId != null}
                                                onChange={(e) =>
                                                    setForm({ ...form, roleCode: e.target.value.toUpperCase() })
                                                }
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Type</label>
                                            <select
                                                className="form-select"
                                                value={form.type}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        type: e.target.value as 'INTERNAL' | 'EXTERNAL',
                                                    })
                                                }
                                            >
                                                <option value="EXTERNAL">EXTERNAL</option>
                                                <option value="INTERNAL">INTERNAL</option>
                                            </select>
                                        </div>
                                        <div className="col-md-12">
                                            <label className="form-label">Display name</label>
                                            <input
                                                className="form-control flex-1"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="row justify-content-center align-items-center mt-4">
                                        <div className="col-auto">
                                            <button
                                                type="button"
                                                className="btn btn-cancel"
                                                onClick={() => setShowModal(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                        <div className="col-auto">
                                            <button type="submit" className="btn btn-green max-135" disabled={busy}>
                                                <span>{editId == null ? 'Save' : 'Update'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </RbacPageShell>
    );
};

export default RolesPage;
